import { downloadUrl } from "../../api/client";
import { useEvidencePacks } from "../../api/hooks";
import { formatDateTime } from "../../api/types";
import { Disclosure, EmptyState, Table, type Column } from "../../components";
import { t } from "../../i18n/t";

export interface EvidencePackRow {
  id: string;
  created_at: string;
  sha256: string;
  object_key: string;
  object_store: string | null;
  retention_until: string | null;
  /** A plain integer, NOT `MoneyStr` (§A8.9). It is never rendered by `<Money>`. */
  size_bytes: number | null;
  /**
   * Round-4 review, finding 5 (LOW): `NULL` means this IS the pack a customer should download; a
   * timestamp means a NEWER pack has since superseded it (`build_evidence_pack`'s own docstring,
   * `workflow/evidence.py`). Optional (`?`) rather than required so this row type keeps working for a
   * server response from BEFORE 0020 added the column — `useEvidencePacks`'s live data always has it.
   */
  superseded_at?: string | null;
}

function isExpired(pack: EvidencePackRow): boolean {
  return pack.retention_until !== null && Date.parse(pack.retention_until) <= Date.now();
}

/**
 * §B6.3 and §A8.9. The list is a pure row read — no object-store I/O anywhere, because every field is a column.
 * The download element is exactly what §B6.3 says it is: a plain `<a href>` built from 06a's `downloadUrl`, so
 * keyboard, right-click, middle-click and the same-origin session cookie all work and nothing in this bundle
 * fetches a zip (§B1.1's one-fetch-site rule).
 *
 * A pack past its retention renders "expired and deleted" with **no link**, which is the §A8.4 `410 gone` case
 * prevented rather than reported; the standing `evidence.cli_hint` sentence names `lucimig evidence --out`, which
 * is §A8.4's remedy for the `413 payload_too_large` case.
 *
 * §B6.4: there is no per-migration purge action here. Endpoint 46 does not exist (§Z12); the global sweep lives
 * on `/settings` (task 06b).
 */
export function EvidencePanel({ migrationId }: { migrationId: string }): JSX.Element {
  const packs = useEvidencePacks(migrationId);
  const items = (packs.data?.items ?? []) as unknown as readonly EvidencePackRow[];

  const columns: readonly Column<EvidencePackRow>[] = [
    {
      key: "created_at",
      header: t("evidence.col_created"),
      render: (pack) => formatDateTime(pack.created_at),
    },
    { key: "sha256", header: t("evidence.col_sha256"), render: (pack) => <code>{pack.sha256}</code> },
    {
      key: "size_bytes",
      header: t("evidence.col_size"),
      numeric: true,
      render: (pack) => (
        <span data-testid={`evidence-size-${pack.id}`}>{pack.size_bytes ?? t("common.unknown")}</span>
      ),
    },
    {
      key: "retention_until",
      header: t("evidence.col_retention"),
      render: (pack) => (
        <span data-testid={`evidence-retention-${pack.id}`}>
          {pack.retention_until === null ? t("common.unknown") : formatDateTime(pack.retention_until)}
        </span>
      ),
    },
    {
      key: "object_store",
      header: t("evidence.col_store"),
      render: (pack) => pack.object_store ?? t("common.unknown"),
    },
    {
      key: "status",
      header: t("evidence.col_status"),
      render: (pack) => (
        <span data-testid={`evidence-status-${pack.id}`}>
          {pack.superseded_at ? t("evidence.superseded") : t("evidence.current")}
        </span>
      ),
    },
    {
      key: "download",
      header: t("evidence.col_download"),
      render: (pack) => (
        <span data-testid={`evidence-action-${pack.id}`}>
          {isExpired(pack) ? (
            t("evidence.expired")
          ) : (
            <a href={downloadUrl(migrationId, pack.id)}>{t("evidence.download", { id: pack.id })}</a>
          )}
        </span>
      ),
    },
  ];

  return (
    <Disclosure
      asHeading
      title={t("evidence.title")}
      counts={items.length === 0 ? t("evidence.counts_none") : t("evidence.counts", { n: items.length })}
      testId="status-evidence-section"
    >
      {items.length === 0 ? (
        <EmptyState title={t("evidence.empty")} />
      ) : (
        <>
          <Table
            caption={t("evidence.title")}
            columns={columns}
            rows={items}
            rowKey={(pack) => pack.id}
            emptyMessage={t("evidence.empty")}
          />
          <p data-testid="evidence-cli-hint">{t("evidence.cli_hint")}</p>
        </>
      )}
    </Disclosure>
  );
}
